import React, { useState, useRef, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Timer, Plus, MoreVertical, Trash2, Edit2 } from 'lucide-react';
import { Button } from '@/shadcn/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/shadcn/components/ui/popover';
import { FlowNodeData } from '../types';
import { cn } from '@/shadcn/lib/utils';

interface DelayNodeV2Props {
  data: FlowNodeData;
}

const formatDelayTime = (durationMs?: number) => {
  if (!durationMs) return 'Not configured';

  const totalSeconds = durationMs / 1000;
  const totalMinutes = totalSeconds / 60;
  const totalHours = totalMinutes / 60;
  const totalDays = totalHours / 24;

  // For days >= 1, display in days (with decimals if needed)
  if (totalDays >= 1) {
    const displayDays = totalDays % 1 === 0 ? Math.floor(totalDays) : Number(totalDays.toFixed(1));
    return `${displayDays} ${displayDays === 1 ? 'day' : 'days'}`;
  }

  // For hours >= 1 but < 24, display in hours (with decimals if needed)
  if (totalHours >= 1) {
    const displayHours = totalHours % 1 === 0 ? Math.floor(totalHours) : Number(totalHours.toFixed(1));
    return `${displayHours} ${displayHours === 1 ? 'hour' : 'hours'}`;
  }

  // For minutes >= 1 but < 60, display in minutes (with decimals if needed)
  if (totalMinutes >= 1) {
    const displayMinutes = totalMinutes % 1 === 0 ? Math.floor(totalMinutes) : Number(totalMinutes.toFixed(1));
    return `${displayMinutes} ${displayMinutes === 1 ? 'minute' : 'minutes'}`;
  }

  // For seconds
  const displaySeconds = totalSeconds % 1 === 0 ? Math.floor(totalSeconds) : Number(totalSeconds.toFixed(1));
  return `${displaySeconds} ${displaySeconds === 1 ? 'second' : 'seconds'}`;
};

export const DelayNodeV2: React.FC<DelayNodeV2Props> = ({ data }) => {
  const {
    nodeId,
    metadata,
    isEditing,
    isSelected,
    isRoot,
    onNodeClick,
    onDeleteNode,
  } = data;

  const [showOptionsPopover, setShowOptionsPopover] = useState(false);
  const popoverRef = useRef<HTMLDivElement>(null);

  const handleNodeClick = () => {
    onNodeClick?.(nodeId);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(false);
    onDeleteNode?.(nodeId);
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowOptionsPopover(!showOptionsPopover);
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setShowOptionsPopover(false);
      }
    };

    if (showOptionsPopover) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showOptionsPopover]);

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open properties panel by selecting this node
    onNodeClick?.(nodeId);
  };

  const durationMs = metadata?.durationMs;
  const isConfigured = durationMs && durationMs > 0;

  return (
    <div className="relative">
      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Top}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />

      {/* Main node container */}
      <div className="flex flex-col items-start justify-center relative w-[342px] transition-all duration-200">
        {/* Node with border */}
        <div className={cn(
          'w-full bg-white border border-gray-200 rounded-2xl transition-all duration-200',
          isSelected && 'border-black border-2'
        )}
        >
          {/* Header Card */}
          <div
            className="w-full bg-white cursor-pointer rounded-t-2xl"
            onClick={handleNodeClick}
          >
            <div className="flex h-12 items-center gap-1 p-3 relative w-full">
              <div className="flex justify-between flex-1 self-stretch grow items-center relative">
                <div className="inline-flex items-center gap-2 relative flex-[0_0_auto]">
                  <div className="bg-yellow-500 flex w-6 h-6 items-center justify-center relative rounded-lg">
                    <Timer className="w-4 h-4 text-white" />
                  </div>
                  <div className="relative w-fit font-medium text-gray-900 text-sm tracking-[0] leading-[22px] whitespace-nowrap">
                    Time Delay
                  </div>
                </div>
                <div className="inline-flex gap-2 flex-[0_0_auto] items-center relative">
                  {/* Hide options menu for root delay nodes since they cannot be deleted */}
                  {!isRoot && (
                    <div ref={popoverRef}>
                      <Popover open={showOptionsPopover} onOpenChange={setShowOptionsPopover}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="flex flex-col w-6 h-6 items-center justify-center gap-2 px-2 py-0.5 bg-white rounded-lg hover:bg-gray-100"
                            onClick={handleOptionsClick}
                          >
                            <MoreVertical className="w-3 h-3 text-gray-600" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-1" align="end">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={handleDelete}
                          >
                            <Trash2 className="w-3 h-3" />
                            Delete Node
                          </Button>
                        </PopoverContent>
                      </Popover>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Separator line */}
          <div className="w-full h-px bg-gray-200" />

          {/* Content Card */}
          <div className="w-full bg-white rounded-b-2xl overflow-hidden">
            <div className="flex flex-col items-start gap-3 px-3 py-3 relative w-full bg-white">
              {isConfigured ? (
                <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-gray-100 rounded-xl overflow-hidden">
                  <div className="text-sm font-medium text-gray-700">
                    Wait for {formatDelayTime(durationMs)}
                  </div>
                  {isEditing && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-2 p-1.5 flex w-6 h-6 items-center justify-center relative rounded-lg hover:bg-gray-200"
                    onClick={handleEditClick}
                  >
                    <Edit2 className="w-3 h-3 text-gray-600" />
                  </Button>
                )}
                </div>
            ) : (
              <div className="flex items-center justify-between px-3 py-2 relative self-stretch w-full flex-[0_0_auto] bg-yellow-50 rounded-xl overflow-hidden">
                <Button
                  variant="outline"
                  className="inline-flex flex-col items-center gap-2 px-2 py-0.5 relative flex-[0_0_auto] bg-white rounded-lg border border-dashed border-gray-300 hover:bg-gray-50 h-auto"
                  onClick={handleEditClick}
                >
                  <div className="gap-2 inline-flex items-center relative flex-[0_0_auto]">
                    <Plus className="w-3 h-3 text-gray-600" />
                    <div className="relative w-fit mt-[-1.00px] font-medium text-gray-600 text-xs tracking-[0] leading-[1.5] whitespace-nowrap">
                      Add Delay Time
                    </div>
                  </div>
                </Button>
              </div>
            )}
            </div>
          </div>
        </div>

        {/* Bottom connector with add button - positioned to show half inside border */}
        {isEditing && (
          <div className="relative w-full flex justify-center -mt-3 z-10">
            <Button
              size="sm"
              className="flex w-6 h-6 items-center justify-center p-0 bg-gray-600 rounded-full border-2 border-white hover:bg-gray-700 shadow-md"
              onClick={(e) => {
                e.stopPropagation();
                const rect = e.currentTarget.getBoundingClientRect();
                data.onShowOptionBar?.(nodeId, {
                  x: rect.left + rect.width / 2,
                  y: rect.top
                });
              }}
            >
              <Plus className="w-3 h-3 text-white" />
            </Button>
          </div>
        )}
      </div>

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Bottom}
        isConnectable={false}
        className="w-3 h-3 bg-gray-400 border-2 border-white"
      />
    </div>
  );
};
